import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import { FeaturedProCard } from "@/components/featured/FeaturedProCard";

const GAP = 16;
const MIN_CARD = 250;
const MAX_VISIBLE = 4;
const BG = "linear-gradient(135deg, #1B3F72 0%, #1E4680 55%, #22508A 100%)";

// Visible cards = as many ≥250px slots as fit (max 4) — never squeezed to hit a column count.
function useSlots(track, count) {
  const [per, setPer] = useState(1);
  const [edge, setEdge] = useState({ start: true, end: true });
  const sync = useCallback(() => {
    const el = track.current;
    if (!el) return;
    setPer(Math.max(1, Math.min(MAX_VISIBLE, Math.floor((el.clientWidth + GAP) / (MIN_CARD + GAP)))));
    setEdge({ start: el.scrollLeft <= 4, end: el.scrollLeft + el.clientWidth >= el.scrollWidth - 4 });
  }, [track]);
  useEffect(() => {
    const el = track.current;
    if (!el) return undefined;
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    el.addEventListener("scroll", sync, { passive: true });
    return () => { ro.disconnect(); el.removeEventListener("scroll", sync); };
  }, [track, sync, count]);
  return { per, edge };
}

function Arrow({ dir, disabled, onClick, tid }) {
  const Icon = dir < 0 ? ChevronLeft : ChevronRight;
  return (
    <button type="button" onClick={onClick} disabled={disabled} data-testid={tid}
      aria-label={dir < 0 ? "Previous professionals" : "Next professionals"}
      className="flex h-8 w-8 items-center justify-center rounded-full border border-white/25 text-white transition-colors hover:border-[#C79A3B] hover:text-[#C79A3B] disabled:pointer-events-none disabled:opacity-30">
      <Icon className="h-4 w-4" />
    </button>
  );
}

/* Unified Featured Professionals Spotlight (Homepage, Connections, Marketplace). Items arrive
   already fairly ordered by the server; the client never reshuffles. */
export function FeaturedSpotlight({ tid, placement, items, subtitle, headerExtra, renderAction, className = "", inner = "" }) {
  const track = useRef(null);
  const { per, edge } = useSlots(track, items.length);
  const scroll = (dir) => {
    const el = track.current;
    if (el) el.scrollBy({ left: dir * (el.clientWidth + GAP), behavior: "smooth" });
  };
  const onKey = (e) => {
    if (e.key === "ArrowRight") { e.preventDefault(); scroll(1); }
    if (e.key === "ArrowLeft") { e.preventDefault(); scroll(-1); }
  };
  const width = per === 1 ? "min(86%, 360px)" : `calc((100% - ${(per - 1) * GAP}px) / ${per})`;
  const nav = items.length > per;
  return (
    <section data-testid={tid} aria-labelledby={`${tid}-h`} className={`relative overflow-hidden ${className}`} style={{ background: BG }}>
      <div className="absolute inset-x-0 top-0 h-[1.5px] bg-gradient-to-r from-transparent via-[#C79A3B]/80 to-transparent" aria-hidden="true" />
      <div className={inner}>
        <div className="mb-3 flex flex-wrap items-end justify-between gap-x-3 gap-y-2">
          <div className="min-w-0 max-w-2xl">
            <p className="flex flex-wrap items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.2em]" data-testid={`${tid}-eyebrow`}>
              <span className="inline-flex items-center gap-1.5 text-[#D9B45A]"><Sparkles className="h-3.5 w-3.5" /> PropertyConnect Spotlight</span>
              <span aria-hidden="true" className="text-white/40">·</span>
              <span data-testid={`${tid}-sponsored`} className="font-medium normal-case tracking-normal text-white/70">Sponsored</span>
            </p>
            <h2 id={`${tid}-h`} className="mt-0.5 font-display text-xl font-bold leading-tight text-white sm:text-2xl">Featured Professionals</h2>
            {subtitle && <p className="mt-0.5 text-sm text-white/75" data-testid={`${tid}-subtitle`}>{subtitle}</p>}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {nav && (
              <div className="flex gap-2">
                <Arrow dir={-1} disabled={edge.start} onClick={() => scroll(-1)} tid={`${tid}-prev`} />
                <Arrow dir={1} disabled={edge.end} onClick={() => scroll(1)} tid={`${tid}-next`} />
              </div>
            )}
            {headerExtra}
          </div>
        </div>
        <div ref={track} data-testid={`${tid}-track`} tabIndex={0} onKeyDown={onKey} role="region" aria-roledescription="carousel" aria-label="Featured professionals"
          className="flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth pb-1.5 pt-1 outline-none [scrollbar-width:none] focus-visible:ring-2 focus-visible:ring-[#C79A3B]/60 [&::-webkit-scrollbar]:hidden">
          {items.map((c, i) => (
            <div key={c.id} data-testid={`${tid}-slot`} aria-roledescription="slide" aria-label={`${i + 1} of ${items.length}`}
              className="shrink-0 snap-start" style={{ width }}>
              <FeaturedProCard c={c} placement={placement} extraAction={renderAction?.(c)} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default FeaturedSpotlight;
